import React from 'react';
import { CartItemType, ProductOption } from '../types/types';
import { INGREDIENT_IMAGES, MENU_PRODUCTS } from '../data/menuData';
import { MinusIcon, PlusIcon, TrashIcon, EditIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onEdit: (item: CartItemType) => void;
}
export function CartItem({
  item,
  onUpdateQuantity,
  onRemove,
  onEdit
}: CartItemProps) {
  const product = MENU_PRODUCTS.find(p => p.id === item.productId);
  const {
    t,
    formatPriceInline,
    translateProductName,
    translateIngredient
  } = useLanguage();
  const translatedName = translateProductName(item.productId, item.name);
  const getAllSelectedIngredients = () => {
    const all: ProductOption[] = [];
    const s = item.selections;
    if (s.base) all.push(s.base);
    if (s.proteina) all.push(s.proteina);
    if (s.toppings) all.push(...s.toppings);
    if (s.toppingsPremium) all.push(...s.toppingsPremium);
    if (s.aderezos) all.push(...s.aderezos);
    if (s.semillas) all.push(...s.semillas);
    return all;
  };
  const ingredients = getAllSelectedIngredients();
  return <div className="bg-surface p-4 rounded-2xl border border-gray-800 shadow-sm mb-3" data-id="element-727">
      <div className="flex gap-3 mb-3" data-id="element-728">
        {/* Product Image */}
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-surface-light flex-shrink-0 border border-gray-700" data-id="element-729">
          {product?.image ? <img src={product.image} alt={translatedName} className="w-full h-full object-cover" data-id="element-730" /> : <div className="w-full h-full flex items-center justify-center bg-verde/10 text-verde font-bold text-xl" data-id="element-731">
              {translatedName.charAt(0)}
            </div>}
        </div>

        <div className="flex-1" data-id="element-732">
          <div className="flex justify-between items-start mb-1" data-id="element-733">
            <h4 className="font-heading font-semibold text-white leading-tight tracking-wide" data-id="element-734">
              {translatedName}
            </h4>
            <span className="font-bold text-verde whitespace-nowrap ml-2 text-xs flex flex-col items-end" data-id="element-735">
              {formatPriceInline(item.subtotal * item.quantity)}
            </span>
          </div>

          {/* Visual Ingredients List */}
          {ingredients.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-736">
              {ingredients.map((ing, idx) => <div key={`${ing.id}-${idx}`} className="w-6 h-6 rounded-full overflow-hidden border border-gray-700 bg-surface-light" title={translateIngredient(ing.id, ing.name)} data-id="element-737">
                  {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" data-id="element-738" /> : <div className="w-full h-full bg-gray-700" data-id="element-739" />}
                </div>)}
            </div>}

          {item.selections.comentarios && <p className="text-xs text-gray-400 italic mt-2 line-clamp-2" data-id="element-740">
              {t('note')}: {item.selections.comentarios}
            </p>}
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-800" data-id="element-741">
        <div className="flex items-center gap-2" data-id="element-742">
          {item.type === 'bowl' || item.type === 'sandwich' || item.type === 'wrap' ? <button onClick={() => onEdit(item)} className="text-terracota text-sm font-medium flex items-center gap-1 bg-terracota/10 px-3 py-1.5 rounded-lg hover:bg-terracota/20 transition-colors" data-id="element-743">
              <EditIcon className="w-3.5 h-3.5" data-id="element-744" /> {t('edit')}
            </button> : null}
          <button onClick={() => onRemove(item.id)} className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg transition-colors" data-id="element-745">
            <TrashIcon className="w-4 h-4" data-id="element-746" />
          </button>
        </div>

        <div className="flex items-center bg-surface-light rounded-full p-1 border border-gray-700" data-id="element-747">
          <button onClick={() => onUpdateQuantity(item.id, -1)} className="w-7 h-7 rounded-full bg-surface flex items-center justify-center text-white shadow-sm border border-gray-700" data-id="element-748">
            <MinusIcon className="w-3 h-3" data-id="element-749" />
          </button>
          <span className="w-8 text-center font-medium text-sm text-white" data-id="element-750">
            {item.quantity}
          </span>
          <button onClick={() => onUpdateQuantity(item.id, 1)} className="w-7 h-7 rounded-full bg-surface flex items-center justify-center text-white shadow-sm border border-gray-700" data-id="element-751">
            <PlusIcon className="w-3 h-3" data-id="element-752" />
          </button>
        </div>
      </div>
    </div>;
}